// React imports
import React, { useState, useMemo, useCallback, useRef } from 'react';

// Generic import
import _ from 'lodash';
import { rateTypeMismatch } from '../utils/rates.js';
import axios from 'axios';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

// Material UI
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import TextField from '@mui/material/TextField';
import Popover from '@mui/material/Popover';

import { useTheme } from '@mui/material/styles';
import Divider from '@mui/material/Divider';

// FontAwesome
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGlobe as faGlobeThin, faPencil, faHatWizard, faFileContract, faCalendarLinesPen, faClipboard, faPlus, faLinkSlash, faTrashCan, faTriangleExclamation, faArrowsRotate } from '@fortawesome/pro-light-svg-icons';
import { faEnvelope, faPaste } from '@fortawesome/pro-regular-svg-icons';

// Utils
import { calcOfferSum } from '../../utils/opportunity.js';
import { formulaToText } from '../../utils/Formula.js';
import { getMultiplier, calcTotalVolume } from '../../CalcWeight.js';
import currencyToText from '../../CurrencyToText.js';
import { normalizeProductType } from '../utils/product.js';

// Components
import SidePaneOpportunityBuyingRatesDrawer from './SidePane.opportunitybuyingrates.drawer';
import ConfirmDialog from '../components/ConfirmDialog';
import AddCostsManuallyModal from './SidePane.opportunitybuyingrates.addcostsmodal';
import ApiClipboardDialog, { apiOfferToRates } from './SidePane.clipboarddialog';

dayjs.extend(relativeTime);

function isManualBuyingRate(br) {
    return br.platform === -2 || br.platform === undefined || br.platform === null;
}

// AI-parsed RFQ rates carry this platform tag.
function isRfqBuyingRate(br) {
    return br.platform === 'monada-ai-magic';
}

// Rates the user is allowed to edit (charge name / value / calc method): manually
// added rates, plus AI-parsed RFQ rates so users can correct a mis-parsed reply.
function isEditableBuyingRate(br) {
    return isManualBuyingRate(br) || isRfqBuyingRate(br);
}

/**
 * Build a synthetic product that keeps the rate product's ID (so field value
 * keys match) but carries the aggregated weights from all opportunity products.
 */
function buildCalcProducts({ rate, opportunityProducts, organization }) {
    if (!rate.product || !opportunityProducts || opportunityProducts.length === 0) {
        return rate.product ? [rate.product] : opportunityProducts;
    }

    // For sea W/M products: pass all matching opportunity products so W/M is computed across all
    const rateType = normalizeProductType(rate.product.type);
    const isWM = rateType === 'LCL' || rateType.startsWith('By Volume or Weight') || rateType.startsWith('By Dimensions or Weight');
    if (isWM) {
        const wmProducts = opportunityProducts.filter(p => normalizeProductType(p.type) === rateType);
        if (wmProducts.length === 0) return [rate.product];

        const lclProduct = wmProducts.find(p => p.type === 'LCL' && p.calculations);
        if (lclProduct) {
            // Use opportunity's cargo data but rate's densityRatio
            return [{ ...rate.product, calculations: lclProduct.calculations, kgOrLbs: lclProduct.kgOrLbs, cmOrInches: lclProduct.cmOrInches, densityRatio: rate.product.densityRatio || lclProduct.densityRatio, quantity: 1 }];
        }

        if (wmProducts.length === 1) return [{ ...wmProducts[0], id: rate.product.id }];
        // Create synthetic product with total volume and weight from all W/M products
        // keeping rate product ID so field value keys match
        const totalVolume = _.sumBy(wmProducts, p => {
            if (p.volume) return (p.volume || 0) * (p.quantity || 1);
            if (p.length && p.width && p.height) {
                const divisor = (p.cmOrInches || 'cm') === 'cm' ? 1000000 : 1728;
                return (p.quantity || 1) * p.length * p.width * p.height / divisor;
            }
            return 0;
        });
        const totalWeight = _.sumBy(wmProducts, p => (p.weight || 0) * (p.quantity || 1));
        return [{
            ...rate.product,
            volume: totalVolume,
            weight: totalWeight,
            quantity: 1,
            length: undefined, width: undefined, height: undefined,
        }];
    }

    // Air freight (unified) — keep calculations on synthetic product
    const airFreightProduct = opportunityProducts.find(p => p.type === 'Air freight' && p.calculations);
    if (airFreightProduct && normalizeProductType(rate.product.type) === 'Air freight') {
        return [{ ...rate.product, calculations: airFreightProduct.calculations, kgOrLbs: airFreightProduct.kgOrLbs, cmOrInches: airFreightProduct.cmOrInches, roundChargeableWeight: airFreightProduct.roundChargeableWeight, quantity: 1 }];
    }

    // For air freight and containers: use opportunity products (which have current quantities)
    const sameType = opportunityProducts.filter(p => p.type === rate.product.type);
    if (sameType.length === 0) return [rate.product];
    if (sameType.length === 1) return [{ ...sameType[0], id: rate.product.id }];
    const totalWeight = _.sumBy(sameType, p => (p.weight || 0) * (p.quantity || 1));
    const totalCW = _.sum(sameType.map(p => getMultiplier({ product: p, transportationMethod: rate.transportationMethod, allProducts: sameType, round: organization.airRoundChargeableWeight }).multiplier));
    return [{ ...rate.product, weight: totalWeight, quantity: 1, chargeableWeight: totalCW, volume: undefined, length: undefined, width: undefined, height: undefined }];
}

function getSourceIcon(rate) {
    if (rate._rfqSource === true) return faEnvelope;
    if (rate.attributes?.conversationId || rate.attributes?.internetMessageId) return faEnvelope;
    if (rate.attributes?.sourceContent) return faPaste;
    if (rate.platform === -1) return faFileContract;
    if (rate.platform > 0) return faGlobeThin;
    if (rate.platform === 'monada-ai-magic') return faHatWizard;
    return faCalendarLinesPen;
}

function getFieldValue(field, rateProductId) {
    if (!field?.values) return null;
    if (field.type === 'string') {
        const str = field.values.string?.value;
        return str ? { string: str } : null;
    }
    if (field.type === 'flat') {
        return field.values.flat || null;
    }
    // Per-unit / per-unit-type / per-unit-weight / per-unit-type-weight:
    // prefer the value keyed by the rate's own product id (that's where edits land).
    // Placeholder/stray entries under other keys can hold the org's default currency (e.g. USD)
    // and trick the display.
    const perProduct = _.omit(field.values, ['flat', 'string']);
    const val = (rateProductId && perProduct[rateProductId])
        || Object.values(perProduct).find(v => (v?.value != null || v?.formula != null) && v?.currency)
        || Object.values(perProduct).find(v => v?.value != null || v?.formula != null)
        || Object.values(perProduct).find(v => v?.currency);
    if (!val) return null;
    return val;
}

function isFieldLinkedInOffer(offer, rateId, fieldId) {
    if (!offer) return false;
    for (const section of offer.sections || []) {
        for (const o of section.offers || []) {
            for (const f of o.fields || []) {
                if ((f.buyingRates || []).some(br => br.rateId === rateId && br.fieldId === fieldId)) {
                    return true;
                }
            }
        }
    }
    return false;
}

function DensityRatioLabel({ densityRatio, mismatch, oppDensity, onUpdate }) {
    const [open, setOpen] = useState(false);
    const [editValue, setEditValue] = useState(densityRatio);
    const anchorRef = useRef(null);

    return (
        <>
            <span
                ref={anchorRef}
                style={{ fontWeight: 400, fontSize: '10px', letterSpacing: 0, cursor: onUpdate ? 'pointer' : 'default', borderBottom: onUpdate ? '1px dashed #94a3b8' : 'none' }}
                onClick={(e) => { e.stopPropagation(); if (onUpdate) { setEditValue(densityRatio); setOpen(true); } }}
            >
                {' '}Density Ratio {densityRatio}
                {mismatch && (
                    <Tooltip title={`Opportunity density ratio is ${oppDensity}, but this rate uses ${densityRatio}`} arrow>
                        <span style={{ marginLeft: '4px', color: '#ed6c02' }}>
                            <FontAwesomeIcon icon={faTriangleExclamation} style={{ fontSize: '14px' }} />
                        </span>
                    </Tooltip>
                )}
            </span>
            {onUpdate && (
                <Popover
                    open={open}
                    anchorEl={anchorRef.current}
                    onClose={(e) => { if (e) e.stopPropagation(); setOpen(false); }}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    onClick={(e) => e.stopPropagation()}
                    sx={{ zIndex: 1000001 }}
                >
                    <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TextField
                            size="small"
                            label="Density Ratio"
                            type="number"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            sx={{ width: '120px' }}
                            autoFocus
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    onUpdate(parseFloat(editValue) || 1000);
                                    setOpen(false);
                                }
                            }}
                        />
                        <Button
                            size="small"
                            variant="contained"
                            onClick={() => {
                                onUpdate(parseFloat(editValue) || 1000);
                                setOpen(false);
                            }}
                        >
                            Save
                        </Button>
                    </Box>
                </Popover>
            )}
        </>
    );
}

function CostCard({ rate, isInOffer, totals, onClick, onDelete, onEdit, offer, onUnlink, onUnlinkField, onDeleteField, products, organization, opportunityDensityRatio, onUpdateDensityRatio, orphaned }) {
    const icon = getSourceIcon(rate);
    const isManual = isManualBuyingRate(rate);
    const isEditable = isEditableBuyingRate(rate);
    const rateFields = _.flatten((rate.offer?.sections || []).map(s => s.offers?.[0]?.fields || []));
    const rateMultiplierText = (() => {
        if (!rate.product) return 'unit';
        try { return getMultiplier({ product: rate.product, transportationMethod: rate.transportationMethod, allProducts: products || [rate.product] }).multiplierText || 'unit'; }
        catch { return 'unit'; }
    })();

    return (
        <Box
            onClick={onClick}
            sx={{
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                padding: '16px',
                marginBottom: '12px',
                border: orphaned ? '1px solid #fca5a5' : '1px solid #e2e8f0',
                backgroundColor: orphaned ? '#fef2f2' : undefined,
                borderRadius: '8px',
                cursor: 'pointer',
                minHeight: '64px',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                    backgroundColor: '#f8fafc',
                    borderColor: '#cbd5e1'
                },
                '&:hover .cost-card-action': {
                    opacity: 1
                }
            }}
        >
            {/* Primary action button — top right, visible on hover */}
            <IconButton
                className='cost-card-action'
                onClick={(e) => {
                    e.stopPropagation();
                    if (isInOffer && onUnlink) {
                        onUnlink();
                    } else {
                        onDelete(rate);
                    }
                }}
                sx={{
                    position: 'absolute',
                    top: -10,
                    right: -10,
                    opacity: 0,
                    transition: 'opacity 0.15s ease-in-out',
                    width: 28,
                    height: 28,
                    backgroundColor: '#fff',
                    border: '1px solid #e2e8f0',
                    '&:hover': {
                        backgroundColor: isInOffer ? '#fef3c7' : '#fee2e2',
                        borderColor: isInOffer ? '#fcd34d' : '#fca5a5'
                    }
                }}
            >
                <FontAwesomeIcon
                    icon={isInOffer ? faLinkSlash : faTrashCan}
                    style={{ fontSize: isInOffer ? '12px' : '12px', color: isInOffer ? '#d97706' : '#ef4444' }}
                />
            </IconButton>

            {/* Edit button — next to primary action, visible on hover. Manual costs and
                AI-parsed RFQ costs (so a mis-parsed reply can be corrected). */}
            {isEditable && onEdit && (
                <IconButton
                    className='cost-card-action'
                    onClick={(e) => {
                        e.stopPropagation();
                        onEdit(rate);
                    }}
                    sx={{
                        position: 'absolute',
                        top: -10,
                        right: 24,
                        opacity: 0,
                        transition: 'opacity 0.15s ease-in-out',
                        width: 28,
                        height: 28,
                        backgroundColor: '#fff',
                        border: '1px solid #e2e8f0',
                        '&:hover': {
                            backgroundColor: '#e0f2fe',
                            borderColor: '#7dd3fc'
                        }
                    }}
                >
                    <FontAwesomeIcon icon={faPencil} style={{ fontSize: '12px', color: '#3b82f6' }} />
                </IconButton>
            )}

            {/* Orphaned notice — this cost's product link is broken (container/type changed or removed) */}
            {orphaned && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.25, color: '#b45309', fontSize: '11px', fontWeight: 700, fontFamily: 'Poppins, sans-serif' }}>
                    <FontAwesomeIcon icon={faTriangleExclamation} style={{ fontSize: '11px' }} />
                    Not linked to a current product — not applied to the quote
                </Box>
            )}

            {/* Header row: icon + supplier info + total */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {/* Icon */}
                <Box sx={{ marginRight: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '24px' }}>
                    <FontAwesomeIcon
                        icon={icon}
                        style={{
                            fontSize: '20px',
                            color: '#3b82f6'
                        }}
                    />
                </Box>

                {/* Supplier Info */}
                <Box sx={{ flex: 1 }}>
                    <Typography
                        sx={{
                            fontSize: '15px',
                            fontWeight: isInOffer ? 700 : 400,
                            color: '#0f172a',
                            marginBottom: '2px',
                            fontFamily: 'Poppins, sans-serif'
                        }}
                    >
                        {rate.supplier.organization}
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: '11px',
                            fontWeight: 600,
                            color: '#64748b',
                            textTransform: 'uppercase',
                            letterSpacing: '0.1em',
                            fontFamily: 'Poppins, sans-serif'
                        }}
                    >
                        {rate.product.type}
                        {normalizeProductType(rate.product.type) === 'LCL' && (() => {
                            const rateDensity = rate.product.densityRatio || 1000;
                            const oppDensity = opportunityDensityRatio || 1000;
                            const mismatch = rateDensity !== oppDensity;
                            return (
                                <DensityRatioLabel
                                    densityRatio={rateDensity}
                                    mismatch={mismatch}
                                    oppDensity={oppDensity}
                                    onUpdate={onUpdateDensityRatio ? (newVal) => onUpdateDensityRatio(rate, newVal) : null}
                                />
                            );
                        })()}
                    </Typography>
                </Box>

                {/* Totals */}
                {!_.isEmpty(totals?.max?.sum) && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                        {Object.entries(totals.max.sum).map(([currency, sum], index) => (
                            <Box key={currency} sx={{ fontSize: '14px', fontWeight: 600, color: '#1e3a5f', lineHeight: 1.2 }}>
                                {index > 0 ? '+ ' : ''}{currencyToText(currency, true, sum.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 }))}
                            </Box>
                        ))}
                    </Box>
                )}
                {_.isEmpty(totals?.max?.sum) && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', ml: 2 }}>
                        <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#1e3a5f', lineHeight: 1.2 }}>
                            N/A
                        </Typography>
                    </Box>
                )}
            </Box>

            {/* Parsed info + source */}
            {(rate.modified || rate.created || rate.platform === 'monada-ai-magic' || rate.platform === -1 || rate.platform > 0 || rate._rfqSource) && (
                <Box sx={{ display: 'flex', gap: 1, ml: '40px', mt: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                    {(rate.modified || rate.created) ? (
                        <Typography sx={{ fontSize: '10px', color: '#94a3b8' }}>
                            {dayjs(rate.modified || rate.created).fromNow()} ({dayjs(rate.modified || rate.created).format('MMM D, YYYY')})
                        </Typography>
                    ) : null}
                    {rate.platform === 'monada-ai-magic' && !rate._rfqSource && (
                        <Typography sx={{ fontSize: '10px', color: '#8b5cf6', fontWeight: 500 }}>
                            via Copy from Anywhere
                        </Typography>
                    )}
                    {rate.platform === -1 && (
                        <Typography sx={{ fontSize: '10px', color: '#3b82f6', fontWeight: 500 }}>
                            via Rate Card
                        </Typography>
                    )}
                    {rate.platform > 0 && (
                        <Typography sx={{ fontSize: '10px', color: '#0891b2', fontWeight: 500 }}>
                            via Online Search
                        </Typography>
                    )}
                    {rate._rfqSource && (
                        <Typography sx={{ fontSize: '10px', color: '#d97706', fontWeight: 500 }}>
                            via RFQ
                        </Typography>
                    )}
                    {rate.overridden && (
                        <Typography sx={{ fontSize: '10px', color: '#0f766e', fontWeight: 500 }}>
                            · edited
                        </Typography>
                    )}
                    {rate.attributes?.attachments?.length > 0 && (
                        <Tooltip title={rate.attributes.attachments.map(a => a.name).join(', ')} placement='top' arrow>
                            <Typography sx={{ fontSize: '10px', color: '#64748b', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '3px', cursor: 'help' }}>
                                📎 {rate.attributes.attachments.length} {rate.attributes.attachments.length === 1 ? 'file' : 'files'}
                            </Typography>
                        </Tooltip>
                    )}
                </Box>
            )}

            {/* Individual field rows */}
            {rateFields.length > 0 && (
                <Box sx={{ mt: 1.5, ml: '40px', borderTop: '1px solid #e2e8f0', pt: 1 }}>
                    {rateFields.map(field => {
                        const val = getFieldValue(field, rate.product?.id);
                        const linked = isFieldLinkedInOffer(offer, rate.id, field.id);
                        const multiplierSuffix = (() => {
                            if (field.type === 'flat') return '/ shipment';
                            if (field.type === 'string' || field.type === 'custom') return '';
                            if (field.type === 'per-unit-weight' || field.type === 'per-unit-type-weight') return `/ ${rateMultiplierText.replace(/\s*\(cw\)/, '')} (gw)`;
                            if (field.type === 'per-unit' || field.type === 'per-unit-type') return `/ ${rateMultiplierText.includes('(cw)') ? rateMultiplierText : rate.transportationMethod === 'air' ? rateMultiplierText + ' (cw)' : rateMultiplierText}`;
                            return `/ ${rateMultiplierText}`;
                        })();

                        return (
                            <Box
                                key={field.id}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    py: '3px',
                                    '&:hover .field-action': { opacity: 1 }
                                }}
                            >
                                <Typography sx={{ fontSize: '12px', color: '#475569', fontFamily: 'Poppins, sans-serif', mr: 1 }}>
                                    {field.title || 'Untitled'}
                                </Typography>
                                {val && !val.string && val.value != null && !val.formula && (
                                    <Typography sx={{ fontSize: '12px', fontWeight: 500, color: '#1e3a5f', mr: 0.5, fontFamily: 'Poppins, sans-serif', ml: 'auto' }}>
                                        {currencyToText(val.currency, true, val.value?.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 }))}
                                        {multiplierSuffix && <span style={{ color: '#64748b', fontWeight: 400, marginLeft: '4px' }}>{multiplierSuffix}</span>}
                                    </Typography>
                                )}
                                {val && val.formula && (
                                    <Typography sx={{ fontSize: '12px', fontWeight: 500, color: '#64748b', fontStyle: 'italic', mr: 0.5, fontFamily: 'Poppins, sans-serif', ml: 'auto' }}>
                                        {formulaToText({ formula: val.formula, currency: val.currency, multiplierText: rateMultiplierText, errorValue: 'formula' })}
                                        {multiplierSuffix && <span style={{ color: '#64748b', fontWeight: 400, marginLeft: '4px' }}>{multiplierSuffix}</span>}
                                    </Typography>
                                )}
                                {val?.string && (
                                    <Typography sx={{ fontSize: '12px', color: '#64748b', mr: 0.5, fontFamily: 'Poppins, sans-serif' }}>
                                        {val.string.length > 30 ? val.string.slice(0, 30) + '...' : val.string}
                                    </Typography>
                                )}
                                {/* Unlink button for in-offer linked fields */}
                                {isInOffer && linked && onUnlinkField && (
                                    <IconButton
                                        className='field-action'
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onUnlinkField(field.id);
                                        }}
                                        sx={{
                                            opacity: 0,
                                            transition: 'opacity 0.15s ease-in-out',
                                            width: 22,
                                            height: 22,
                                            '&:hover': { backgroundColor: '#fef3c7' }
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faLinkSlash} style={{ fontSize: '10px', color: '#d97706' }} />
                                    </IconButton>
                                )}
                                {/* Delete button for manual rate fields */}
                                {isManual && onDeleteField && (
                                    <IconButton
                                        className='field-action'
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onDeleteField(field.id);
                                        }}
                                        sx={{
                                            opacity: 0,
                                            transition: 'opacity 0.15s ease-in-out',
                                            width: 22,
                                            height: 22,
                                            '&:hover': { backgroundColor: '#fee2e2' }
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faTrashCan} style={{ fontSize: '10px', color: '#ef4444' }} />
                                    </IconButton>
                                )}
                            </Box>
                        );
                    })}
                </Box>
            )}
        </Box>
    );
}

function SidePaneOpportunityBuyingRates(props) {
    const { opportunity, suppliers, organization, accesstoken, exchangeRates, setMinimizedSearch, onUpdateOpportunity, me, unlocode, seaPorts } = props;

    const offers = opportunity?.offers || [];
    const buyingRates = (opportunity?.buyingRates || []).filter(br => br && br.offer && (br.offer?.sections || []).some(s => (s.offers || []).some(o => (o.fields || []).some(f => !_.isEmpty(f.values) && Object.values(f.values).some(v => v?.value != null || v?.formula != null)))));
    const rateRequests = opportunity?.rateRequests || [];
    const transportationMethod = opportunity?.transportationMethod || 'sea';
    const products = opportunity?.products || [];
    const opportunityLCL = products.find(p => p.type === 'LCL');
    const opportunityDensityRatio = opportunityLCL?.densityRatio || 1000;

    function handleUpdateDensityRatio(rate, newDensityRatio) {
        onUpdateOpportunityAndSave({ opportunityId: opportunity.id, func: (op) => {
            const newBuyingRates = (op.buyingRates || []).map(br => {
                if (br.id !== rate.id) return br;
                return { ...br, product: { ...br.product, densityRatio: newDensityRatio } };
            });
            return { ...op, buyingRates: newBuyingRates };
        }});
    }

    const [ ratesMoreInfo, setRatesMoreInfo ] = useState({ open: false });
    const [ deleteConfirm, setDeleteConfirm ] = useState(null);
    const [ addCostsModal, setAddCostsModal ] = useState(null);
    const [ clipboardOpen, setClipboardOpen ] = useState(false);

    // Auto-save: wraps onUpdateOpportunity to also persist buyingRates (and offers) to the server
    const onUpdateOpportunityAndSave = useCallback(({ opportunityId, func }) => {
        onUpdateOpportunity({ opportunityId, func });
        // Compute new state and save to server
        const updated = func(opportunity);
        const doSave = (retries = 0) => {
            axios.put(`/api/opportunities/${opportunity.id}?accesstoken=${accesstoken}&organizationId=${organization.id}`, {
                opportunity: { buyingRates: updated.buyingRates, offers: updated.offers }
            }).catch(e => {
                if ((e.response?.status === 404 || e.response?.status === 400) && retries < 3) {
                    setTimeout(() => doSave(retries + 1), 2000 * (retries + 1));
                } else {
                    console.error('[CostCenter] Auto-save failed:', e);
                }
            });
        };
        doSave();
    }, [opportunity, accesstoken, organization.id, onUpdateOpportunity]);

    const theme = useTheme();

    // Compute per-card totals
    const rateTotals = useMemo(() => {
        const totalsMap = {};
        for (const rate of buyingRates) {
            if (rate.offer) {
                try {
                    const rateProducts = buildCalcProducts({ rate, opportunityProducts: products, organization });
                    totalsMap[rate.id] = calcOfferSum({ offer: rate.offer, products: rateProducts, transportationMethod, exchangeRates, organization });
                } catch (e) {
                    // skip if calc fails
                }
            }
        }
        // Also compute for RFQ rates
        for (const rr of rateRequests) {
            if (rr.rates) {
                for (const rate of rr.rates) {
                    if (rate.offer && !totalsMap[rate.id]) {
                        try {
                            const rateProducts = buildCalcProducts({ rate, opportunityProducts: products, organization });
                            totalsMap[rate.id] = calcOfferSum({ offer: rate.offer, products: rateProducts, transportationMethod, exchangeRates, organization });
                        } catch (e) {
                            // skip
                        }
                    }
                }
            }
        }
        return totalsMap;
    }, [buyingRates, rateRequests, exchangeRates, organization, transportationMethod, products]);

    function onShowRates({ rates: ratesToShow, emphasize }) {
        setRatesMoreInfo({
            open: true,
            rates: ratesToShow,
            emphasize
         });
    }

    function handleDeleteRate(rate) {
        setDeleteConfirm(rate);
    }

    function confirmDeleteRate() {
        const rate = deleteConfirm;
        setDeleteConfirm(null);

        onUpdateOpportunityAndSave({ opportunityId: opportunity.id, func: (op) => {
            // Remove from buyingRates
            const newBuyingRates = (op.buyingRates || []).filter(br => br.id !== rate.id);

            // Remove all references from offer fields
            const newOffers = (op.offers || []).map(offer => ({
                ...offer,
                sections: offer.sections.map(section => ({
                    ...section,
                    offers: section.offers.map(o => ({
                        ...o,
                        fields: o.fields.map(field => ({
                            ...field,
                            buyingRates: (field.buyingRates || []).filter(br => br.rateId !== rate.id)
                        }))
                    }))
                }))
            }));

            return { ...op, buyingRates: newBuyingRates, offers: newOffers };
        }});
    }

    function handleUnlinkRate(rate, offer) {
        onUpdateOpportunityAndSave({ opportunityId: opportunity.id, func: (op) => {
            const newOffers = (op.offers || []).map(o => {
                if (o.id !== offer.id) return o;
                return {
                    ...o,
                    sections: o.sections.map(section => ({
                        ...section,
                        offers: section.offers.map(so => ({
                            ...so,
                            fields: so.fields.map(field => {
                                const remaining = (field.buyingRates || []).filter(br => br.rateId !== rate.id);
                                if (remaining.length === (field.buyingRates || []).length) return field;
                                return { ...field, buyingRates: remaining };
                            })
                        }))
                    }))
                };
            });
            return { ...op, offers: newOffers };
        }});
    }

    function handleUnlinkField(rate, rateFieldId, offer) {
        onUpdateOpportunityAndSave({ opportunityId: opportunity.id, func: (op) => {
            const newOffers = (op.offers || []).map(o => {
                if (o.id !== offer.id) return o;
                return {
                    ...o,
                    sections: o.sections.map(section => ({
                        ...section,
                        offers: section.offers.map(so => ({
                            ...so,
                            fields: so.fields.map(field => {
                                const remaining = (field.buyingRates || []).filter(br => !(br.rateId === rate.id && br.fieldId === rateFieldId));
                                if (remaining.length === (field.buyingRates || []).length) return field;
                                return { ...field, buyingRates: remaining };
                            })
                        }))
                    }))
                };
            });
            return { ...op, offers: newOffers };
        }});
    }

    function handleDeleteManualField(rate, rateFieldId) {
        setDeleteConfirm({ _deleteField: true, rate, rateFieldId });
    }

    function confirmDeleteManualField() {
        const { rate, rateFieldId } = deleteConfirm;
        setDeleteConfirm(null);

        onUpdateOpportunityAndSave({ opportunityId: opportunity.id, func: (op) => {
            // Remove the field from the rate's offer
            let newBuyingRates = _.cloneDeep(op.buyingRates || []);
            const brIndex = newBuyingRates.findIndex(br => br.id === rate.id);
            if (brIndex !== -1) {
                newBuyingRates[brIndex].offer.sections.forEach(s => {
                    if (s.offers?.[0]) {
                        s.offers[0].fields = (s.offers[0].fields || []).filter(f => f.id !== rateFieldId);
                    }
                });
            }

            // Remove offer field references for this specific field
            const newOffers = (op.offers || []).map(offer => ({
                ...offer,
                sections: offer.sections.map(section => ({
                    ...section,
                    offers: section.offers.map(o => ({
                        ...o,
                        fields: o.fields.map(field => {
                            const remaining = (field.buyingRates || []).filter(br => !(br.rateId === rate.id && br.fieldId === rateFieldId));
                            if (remaining.length === (field.buyingRates || []).length) return field;
                            return { ...field, buyingRates: remaining };
                        })
                    }))
                }))
            }));

            return { ...op, buyingRates: newBuyingRates, offers: newOffers };
        }});
    }

    function handleCardClick(rate, offer) {
        if (rate._rfqSource) {
            // RFQ rate — pass directly since it's not in buyingRates
            onShowRates({ rates: [rate] });
        } else if (offer) {
            const fieldBuyingRates = offer.sections.map(section => section.offers.map(offer => offer.fields.map(field => field.buyingRates))).flat().flat().flat();
            const emphasize = fieldBuyingRates.filter(fbr => fbr?.rateId === rate.id);
            onShowRates({
                rates: buyingRates.filter(f => f.id === rate.id),
                emphasize
            });
        } else {
            // Alternative rate with no specific offer context
            onShowRates({ rates: buyingRates.filter(f => f.id === rate.id) });
        }
    }

    if (ratesMoreInfo.open) {
        return (
            <SidePaneOpportunityBuyingRatesDrawer
                onClose={() => setRatesMoreInfo({...ratesMoreInfo, open: false})}
                rates={ratesMoreInfo.rates}
                organization={organization}
                exchangeRates={exchangeRates}
                emphasize={ratesMoreInfo.emphasize}
                setMinimizedSearch={setMinimizedSearch}
                transportationMethod={transportationMethod}
                products={products}
            />
        )
    }

    return (
        <Box sx={{ width: '100%', background: theme.palette.background.default, pb: 3, overflowY: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Box sx={{ width: '100%', background: 'transparent', padding: '6px 24px', mb: 3, borderBottom: '1px solid rgba(59, 130, 246, 0.3)', boxShadow: 'none' }}>
                <Typography variant='body1' sx={{ fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#334155' }}>
                    Cost Center
                </Typography>
            </Box>
            {_.isEmpty(products) && (
                <Alert severity='warning' fullWidth variant='standard' sx={{ width: 'calc(100% - 48px)', mb: 4 }}>
                    Please add products to the opportunity first.
                </Alert>
            )}
            {_.isEmpty(offers) && (
                <Typography variant='caption' style={{ textStyle: 'italic', flex: 1, padding: '12px 0px' }}>
                    No offers yet
                </Typography>
            )}
            {(() => {
                // Compute RFQ-only rates once (deduplicate across rate requests sharing the same thread)
                const buyingRateIds = new Set(buyingRates.map(br => br.id));
                const rfqOnlyRatesMap = {};
                // Durable set of every rate id that originated from an RFQ response.
                // The `_rfqSource` marker below is transient (added only to RFQ rates not
                // yet used in an offer); once a rate is dragged into the offer it's excluded
                // from that map, so without this durable set its source label would flip
                // from "via RFQ" to "via Copy from Anywhere" (both share platform
                // 'monada-ai-magic'). This set lets us re-stamp the marker for in-offer rates.
                const rfqRateIds = new Set();
                for (const rr of rateRequests) {
                    if (rr.rates) {
                        for (const rate of rr.rates) {
                            rfqRateIds.add(rate.id);
                            if (!buyingRateIds.has(rate.id) && !rfqOnlyRatesMap[rate.id]) {
                                rfqOnlyRatesMap[rate.id] = { ...rate, _rfqSource: true };
                            }
                        }
                    }
                }
                const rfqOnlyRates = Object.values(rfqOnlyRatesMap);

                // Compute all buying rate IDs used across ALL offers
                // Only count references that point to valid product IDs still in the opportunity,
                // and to a product of the rate's own container type: a rate linked to a product whose
                // type has since changed is never applied, so it isn't used (it shows as orphaned).
                const opportunityProductIds = new Set(products.map(p => p.id));
                const productsById = _.keyBy(products, 'id');
                const buyingRatesById = _.keyBy(buyingRates, 'id');
                const linkApplies = br => br.rateId && (!br.opportunityProductId
                    || (opportunityProductIds.has(br.opportunityProductId) && !rateTypeMismatch(buyingRatesById[br.rateId], productsById[br.opportunityProductId])));
                const allUsedBuyingRateIds = offers.flatMap(offer =>
                    offer.sections.flatMap(s => s.offers.flatMap(o =>
                        o.fields.flatMap(f => (f.buyingRates || [])
                            .filter(linkApplies)
                            .map(br => br.rateId))
                    ))
                );

                const productTypes = new Set(products.map(p => normalizeProductType(p.type)));
                const globalAlternativeRates = [
                    ...buyingRates.filter(br => !allUsedBuyingRateIds.includes(br.id) || !productTypes.has(normalizeProductType(br.product?.type))),
                    ...rfqOnlyRates
                ];

                // Orphaned costs: a cost that IS referenced by an offer field but whose product
                // link is now broken — the product it was attached to was removed or had its
                // type/container changed (e.g. after a transport-mode switch). It sits in the
                // alternative bucket and is silently NOT applied, so flag it distinctly.
                const referencedRateIds = new Set(offers.flatMap(offer =>
                    offer.sections.flatMap(s => s.offers.flatMap(o =>
                        o.fields.flatMap(f => (f.buyingRates || []).map(br => br.rateId).filter(Boolean))))));
                const orphanedRates = globalAlternativeRates.filter(br => !br._rfqSource && referencedRateIds.has(br.id));
                const orphanedRateIds = new Set(orphanedRates.map(br => br.id));
                const availableAlternativeRates = globalAlternativeRates.filter(br => !orphanedRateIds.has(br.id));

                return <>
                    {offers.map((offer, offerIndex) => {
                        // Collect buying rate IDs referenced in this offer's fields
                        const buyingRateIdsForOffer = offer.sections
                            .flatMap(section => section.offers.flatMap(o =>
                                o.fields.flatMap(field => (field.buyingRates || [])
                                    .filter(linkApplies)
                                    .map(br => br.rateId))
                            ));

                        const inOfferRates = buyingRates.filter(br => buyingRateIdsForOffer.includes(br.id) && productTypes.has(normalizeProductType(br.product?.type)));
                        const routeTitle = offer.title || `Route ${offerIndex + 1}`;

                        return (
                            <Box key={offer.id} sx={{ width: '100%', maxWidth: '800px', margin: '0 auto', padding: '0px 24px' }}>
                                <Paper
                                    sx={{
                                        background: '#ffffff',
                                        borderRadius: '12px',
                                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                                        padding: '40px',
                                        fontFamily: 'Poppins, sans-serif'
                                    }}
                                    elevation={0}
                                >
                                    {/* Header */}
                                    <Box sx={{ marginBottom: '12px' }}>
                                        <Typography
                                            variant='body2'
                                            sx={{
                                                fontSize: '14px',
                                                fontWeight: 500,
                                                color: '#334155',
                                                marginBottom: '4px',
                                                fontFamily: 'Poppins, sans-serif'
                                            }}
                                        >
                                            {routeTitle}
                                        </Typography>
                                        <Typography
                                            variant='caption'
                                            sx={{
                                                fontSize: '13px',
                                                color: '#64748b',
                                                fontFamily: 'Poppins, sans-serif'
                                            }}
                                        >
                                            Offer #{offerIndex + 1}
                                        </Typography>
                                    </Box>

                                    {/* In-Offer Costs Section */}
                                    <Box>
                                        <Typography
                                            variant='caption'
                                            sx={{
                                                fontSize: '11px',
                                                fontWeight: 600,
                                                color: '#64748b',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.05em',
                                                marginBottom: '12px',
                                                display: 'block',
                                                fontFamily: 'Poppins, sans-serif'
                                            }}
                                        >
                                            Used in this Offer
                                        </Typography>
                                        {_.isEmpty(inOfferRates) ? (
                                            <Typography
                                                variant='body2'
                                                sx={{
                                                    fontSize: '14px',
                                                    color: '#64748b',
                                                    fontFamily: 'Poppins, sans-serif'
                                                }}
                                            >
                                                No costs used in this offer
                                            </Typography>
                                        ) : (
                                            inOfferRates.map(br => (
                                                <CostCard
                                                    key={br.id}
                                                    rate={rfqRateIds.has(br.id) ? { ...br, _rfqSource: true } : br}
                                                    isInOffer={true}
                                                    totals={rateTotals[br.id]}
                                                    onClick={() => handleCardClick(br, offer)}
                                                    onDelete={handleDeleteRate}
                                                    onUnlink={() => handleUnlinkRate(br, offer)}
                                                    onUnlinkField={(rateFieldId) => handleUnlinkField(br, rateFieldId, offer)}
                                                    onDeleteField={(rateFieldId) => handleDeleteManualField(br, rateFieldId)}
                                                    onEdit={(rate) => setAddCostsModal({ editRate: rate })}
                                                    offer={offer}
                                                    products={products}
                                                    organization={organization}
                                                    opportunityDensityRatio={opportunityDensityRatio}
                                                    onUpdateDensityRatio={null}
                                                />
                                            ))
                                        )}
                                    </Box>
                                </Paper>
                            </Box>
                        )
                    })}

                    {/* Alternative Costs — consolidated across all offers */}
                    <Box sx={{ width: '100%', maxWidth: '800px', margin: '0 auto', padding: '0px 24px' }}>
                        <Paper
                            sx={{
                                background: '#ffffff',
                                borderRadius: '12px',
                                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                                padding: '40px',
                                fontFamily: 'Poppins, sans-serif'
                            }}
                            elevation={0}
                        >
                            {orphanedRates.length > 0 && (
                                <Box sx={{ marginBottom: '24px' }}>
                                    <Alert severity='warning' icon={<FontAwesomeIcon icon={faTriangleExclamation} />} sx={{ mb: 2, borderRadius: '10px', fontFamily: 'Poppins, sans-serif' }}>
                                        <Typography sx={{ fontSize: '13px', fontWeight: 700, mb: 0.25 }}>
                                            {orphanedRates.length} cost{orphanedRates.length > 1 ? 's are' : ' is'} no longer linked to a product
                                        </Typography>
                                        <Typography sx={{ fontSize: '12px', color: '#78350f' }}>
                                            {orphanedRates.length > 1 ? 'These were' : 'This was'} entered against a container/product that has since been changed or removed, so {orphanedRates.length > 1 ? "they're" : "it's"} <b>not applied to the quote</b>. Re-add {orphanedRates.length > 1 ? 'them' : 'it'} to a current product, or delete {orphanedRates.length > 1 ? 'them' : 'it'}.
                                        </Typography>
                                    </Alert>
                                    {orphanedRates.map(br => (
                                        <CostCard
                                            key={br.id}
                                            orphaned={true}
                                            rate={rfqRateIds.has(br.id) && !br._rfqSource ? { ...br, _rfqSource: true } : br}
                                            isInOffer={false}
                                            totals={rateTotals[br.id]}
                                            onClick={() => handleCardClick(br, null)}
                                            onDelete={handleDeleteRate}
                                            onDeleteField={(rateFieldId) => handleDeleteManualField(br, rateFieldId)}
                                            onEdit={(rate) => setAddCostsModal({ editRate: rate })}
                                            offer={null}
                                            products={products}
                                            organization={organization}
                                            opportunityDensityRatio={opportunityDensityRatio}
                                            onUpdateDensityRatio={null}
                                        />
                                    ))}
                                </Box>
                            )}
                            {availableAlternativeRates.length > 0 && (
                                <Box sx={{ marginBottom: '24px' }}>
                                    <Typography
                                        variant='caption'
                                        sx={{
                                            fontSize: '11px',
                                            fontWeight: 600,
                                            color: '#64748b',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.05em',
                                            marginBottom: '12px',
                                            display: 'block',
                                            fontFamily: 'Poppins, sans-serif'
                                        }}
                                    >
                                        Alternative Costs
                                    </Typography>
                                    {availableAlternativeRates.map(br => (
                                        <CostCard
                                            key={br.id}
                                            rate={rfqRateIds.has(br.id) && !br._rfqSource ? { ...br, _rfqSource: true } : br}
                                            isInOffer={false}
                                            totals={rateTotals[br.id]}
                                            onClick={() => handleCardClick(br, null)}
                                            onDelete={handleDeleteRate}
                                            onDeleteField={(rateFieldId) => handleDeleteManualField(br, rateFieldId)}
                                            onEdit={(rate) => setAddCostsModal({ editRate: rate })}
                                            offer={null}
                                            products={products}
                                            organization={organization}
                                            opportunityDensityRatio={opportunityDensityRatio}
                                            onUpdateDensityRatio={null}
                                        />
                                    ))}
                                </Box>
                            )}

                            {/* Add costs from anywhere — requires products on the opportunity */}
                            <Box
                                onClick={() => { if (!_.isEmpty(products)) setClipboardOpen(true); }}
                                sx={{
                                    marginTop: globalAlternativeRates.length > 0 ? '0px' : undefined,
                                    padding: '24px',
                                    borderRadius: '12px',
                                    border: '2px dashed #cbd5e1',
                                    cursor: _.isEmpty(products) ? 'not-allowed' : 'pointer',
                                    opacity: _.isEmpty(products) ? 0.5 : 1,
                                    transition: 'all 0.2s ease-in-out',
                                    fontFamily: 'Poppins, sans-serif',
                                    '&:hover': _.isEmpty(products) ? {} : {
                                        borderColor: '#93c5fd',
                                        backgroundColor: '#f8fafc',
                                    }
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 1.5 }}>
                                    <Box sx={{
                                        width: '48px', height: '48px', borderRadius: '12px',
                                        background: '#f8fafc', border: '1px solid #e2e8f0',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        position: 'relative'
                                    }}>
                                        <FontAwesomeIcon icon={faClipboard} style={{ color: '#475569', fontSize: '18px' }} />
                                        <FontAwesomeIcon icon={faPlus} style={{ color: '#475569', fontSize: '10px', position: 'absolute', bottom: '8px', right: '8px' }} />
                                    </Box>
                                    <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#0f172a', fontFamily: 'Poppins, sans-serif' }}>
                                        Add costs from anywhere
                                    </Typography>
                                    <Typography sx={{ fontSize: '13px', color: '#64748b', fontFamily: 'Poppins, sans-serif' }}>
                                        Paste free text, emails, or sheets
                                    </Typography>
                                </Box>
                            </Box>

                            <Box sx={{ marginTop: '16px', display: 'flex', justifyContent: 'center' }}>
                                <Button
                                    variant='outlined'
                                    disabled={_.isEmpty(products)}
                                    onClick={() => { if (!_.isEmpty(products)) setAddCostsModal({}); }}
                                    sx={{
                                        textTransform: 'uppercase',
                                        fontSize: '12px',
                                        fontWeight: 600,
                                        letterSpacing: '0.05em',
                                        color: '#3b82f6',
                                        borderColor: '#3b82f6',
                                        fontFamily: 'Poppins, sans-serif',
                                    }}
                                >
                                    Add Costs Manually
                                </Button>
                            </Box>
                        </Paper>
                    </Box>
                </>;
            })()}

            <ConfirmDialog
                open={!!deleteConfirm}
                onClose={() => setDeleteConfirm(null)}
                onConfirm={deleteConfirm?._deleteField ? confirmDeleteManualField : confirmDeleteRate}
                color='error'
                title={deleteConfirm?._deleteField ? undefined : 'Delete Master Cost?'}
                sx={{ zIndex: 1000005 }}
            >
                {deleteConfirm?._deleteField
                    ? 'Are you sure you want to permanently delete this charge?'
                    : 'Are you sure you want to permanently delete this cost from the entire offer? This will also remove it from any sections in the quote where it is currently linked. This action cannot be undone.'
                }
            </ConfirmDialog>

            <AddCostsManuallyModal
                open={!!addCostsModal}
                editRate={addCostsModal?.editRate || null}
                onCancel={() => setAddCostsModal(null)}
                onDone={({ newRates }) => {
                    setAddCostsModal(null);
                    onUpdateOpportunityAndSave({ opportunityId: opportunity.id, func: (op) => {
                        let updatedBuyingRates = _.cloneDeep(op.buyingRates || []);
                        let updatedOffers = op.offers;

                        for (const rate of newRates) {
                            if (rate._deleteRate) {
                                // Delete the rate and remove all offer field references
                                const rateId = rate._deleteRate;
                                updatedBuyingRates = updatedBuyingRates.filter(br => br.id !== rateId);
                                updatedOffers = (updatedOffers || []).map(offer => ({
                                    ...offer,
                                    sections: offer.sections.map(section => ({
                                        ...section,
                                        offers: section.offers.map(o => ({
                                            ...o,
                                            fields: o.fields.map(field => ({
                                                ...field,
                                                buyingRates: (field.buyingRates || []).filter(br => br.rateId !== rateId)
                                            }))
                                        }))
                                    }))
                                }));
                            } else if (rate._replaceIn) {
                                let existing = updatedBuyingRates.find(br => br.id === rate._replaceIn);
                                if (!existing) {
                                    // RFQ-only rate (lives in rateRequests[].rates, not yet a buying
                                    // rate). Promote a copy into buyingRates so the edit is user-owned
                                    // and the RFQ auto-poll (which preserves buyingRates verbatim)
                                    // can't overwrite it. Same id keeps offer links + "via RFQ" label.
                                    for (const rr of (op.rateRequests || [])) {
                                        const src = (rr.rates || []).find(x => x.id === rate._replaceIn);
                                        if (src) { existing = _.cloneDeep(src); updatedBuyingRates.push(existing); break; }
                                    }
                                }
                                if (existing && existing.offer?.sections?.[0]?.offers?.[0]) {
                                    // Clear fields from all sections, then set new fields on the first
                                    existing.offer.sections.forEach(s => { if (s.offers?.[0]) s.offers[0].fields = []; });
                                    existing.offer.sections[0].offers[0].fields = rate._newFields;
                                    if (rate._supplierUpdate) {
                                        existing.supplier = rate._supplierUpdate;
                                    }
                                    existing.modified = Date.now();
                                    if (!existing.created) existing.created = existing.modified;
                                    // Mark AI-parsed rates as user-corrected so a later re-parse knows
                                    // not to silently clobber the manual override.
                                    if (existing.platform === 'monada-ai-magic') existing.overridden = true;
                                }
                            } else if (rate._mergeInto) {
                                const existing = updatedBuyingRates.find(br => br.id === rate._mergeInto);
                                if (existing) {
                                    existing.offer.sections[0].offers[0].fields.push(...rate._newFields);
                                    existing.modified = Date.now();
                                    if (!existing.created) existing.created = existing.modified;
                                }
                            } else {
                                updatedBuyingRates.push(rate);
                            }
                        }

                        return { ...op, buyingRates: updatedBuyingRates, offers: updatedOffers };
                    }});
                }}
                suppliers={suppliers}
                products={products}
                organization={organization}
                transportationMethod={transportationMethod}
                existingBuyingRates={buyingRates}
                routes={opportunity?.routes || []}
            />

            {clipboardOpen && (
                <ApiClipboardDialog
                    onClose={() => setClipboardOpen(false)}
                    accesstoken={accesstoken}
                    org={organization}
                    me={me}
                    routes={opportunity?.routes || []}
                    suppliers={suppliers}
                    transportationMethod={transportationMethod}
                    products={products}
                    unlocode={unlocode}
                    seaPorts={seaPorts}
                    setBuyingRates={(func) => {
                        onUpdateOpportunityAndSave({ opportunityId: opportunity.id, func: (op) => {
                            const newBuyingRates = func(op.buyingRates || []);
                            return { ...op, buyingRates: newBuyingRates };
                        }});
                    }}
                />
            )}
        </Box>
    )
}

export default SidePaneOpportunityBuyingRates;
